import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/layout';
import { useMessagingFlow } from '@/stackflow';
import { useCreateChannel } from '../hooks/useMessaging';

const CATEGORIES = ['general', 'announcements', 'treatment', 'admin', 'social'] as const;
type Category = typeof CATEGORIES[number];

export default function NewChannelPage() {
  const { t } = useTranslation();
  const { pop, replace } = useMessagingFlow();
  const createChannel = useCreateChannel();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category>('general');
  const [isPublic, setIsPublic] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    // Validation
    if (!name.trim()) {
      setError(t('messaging.channel.nameRequired'));
      return;
    }

    if (name.length > 50) {
      setError(t('messaging.channel.nameTooLong'));
      return;
    }

    if (description.length > 200) {
      setError(t('messaging.channel.descriptionTooLong'));
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await createChannel.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        isPublic,
      });

      // Navigate to new channel
      replace('ChatActivity', { conversationId: result.channel.id });
    } catch (err: any) {
      setError(err?.message || t('messaging.channel.createError'));
      setIsLoading(false);
    }
  };

  const topBar = (
    <>
      <button
        onClick={() => pop()}
        className="flex items-center justify-center w-10 h-10 rounded-full border border-white/20 bg-white/30 transition-all active:scale-95"
        aria-label={t('common.back')}
      >
        <svg className="h-5 w-5 text-neutral-700 rtl:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <h1 className="flex-1 text-center text-lg sm:text-2xl font-semibold tracking-wide text-neutral-800">
        {t('messaging.channel.createNew')}
      </h1>
      <div className="w-10" />
    </>
  );

  const bottomBar = (
    <div className="px-4 py-3">
      <button
        onClick={handleCreate}
        disabled={isLoading || !name.trim()}
        className="w-full h-12 rounded-xl bg-slate-700 border border-slate-400 font-medium text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('messaging.channel.creating') : t('messaging.channel.create')}
      </button>
    </div>
  );

  return (
    <PageLayout topBar={topBar} bottomBar={bottomBar}>
      <div className="p-4 space-y-6">
        {/* Error message */}
        {error && (
          <div className="p-4 bg-error-50 border border-error-300 rounded-xl">
            <p className="font-medium text-error-900">{error}</p>
          </div>
        )}

        {/* Channel Name */}
        <div className="space-y-2">
          <label htmlFor="channel-name" className="block font-medium text-neutral-800">
            {t('messaging.channel.name')}
            <span className="text-error-500 ml-1">*</span>
          </label>
          <input
            id="channel-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            placeholder={t('messaging.channel.namePlaceholder')}
            className="w-full px-4 py-3 rounded-xl glass-surface border border-white/30 font-medium text-neutral-800 focus:outline-none focus:border-slate-400 focus:shadow-sm transition-all"
          />
          <p className="text-xs text-neutral-500">
            {name.length}/50
          </p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label htmlFor="channel-description" className="block font-medium text-neutral-800">
            {t('messaging.channel.description')}
          </label>
          <textarea
            id="channel-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
            rows={4}
            placeholder={t('messaging.channel.descriptionPlaceholder')}
            className="w-full px-4 py-3 rounded-xl glass-surface border border-white/30 font-medium text-neutral-800 focus:outline-none focus:border-slate-400 focus:shadow-sm transition-all resize-none"
          />
          <p className="text-xs text-neutral-500">
            {description.length}/200
          </p>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <label htmlFor="channel-category" className="block font-medium text-neutral-800">
            {t('messaging.channel.category')}
          </label>
          <div className="relative">
            <select
              id="channel-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              className="w-full px-4 py-3 rounded-xl glass-surface border border-white/30 font-medium text-neutral-800 focus:outline-none focus:border-slate-400 focus:shadow-sm transition-all appearance-none cursor-pointer"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {t(`messaging.channel.categories.${cat}`)}
                </option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Public/Private Toggle */}
        <div className="space-y-2">
          <label className="block font-medium text-neutral-800">
            {t('messaging.channel.visibility')}
          </label>
          <button
            onClick={() => setIsPublic(!isPublic)}
            className="flex items-center gap-4 w-full p-4 rounded-xl glass-surface border border-white/30 transition-all hover:bg-white/20"
          >
            <div
              className={`relative w-14 h-8 rounded-full border border-white/30 transition-colors ${
                isPublic ? 'bg-slate-700' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 bg-white rounded-full border border-white/30 transition-transform ${
                  isPublic ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-neutral-800">
                {isPublic
                  ? t('messaging.channel.public')
                  : t('messaging.channel.private')}
              </p>
              <p className="text-xs text-neutral-500">
                {isPublic
                  ? t('messaging.channel.publicDescription')
                  : t('messaging.channel.privateDescription')}
              </p>
            </div>
          </button>
        </div>

        {/* Info box */}
        <div className="p-4 rounded-xl glass-tinted border border-slate-200">
          <div className="flex gap-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
              <circle cx="12" cy="12" r="10" stroke="var(--color-neutral-700)" strokeWidth="2" />
              <path d="M12 8V12" stroke="var(--color-neutral-700)" strokeWidth="2" strokeLinecap="square" />
              <circle cx="12" cy="16" r="1" fill="var(--color-neutral-700)" />
            </svg>
            <p className="text-sm font-medium text-neutral-800">
              {t('messaging.channel.infoText')}
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
